import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DashLayout } from "@/components/DashLayout";
import { PayoutTable } from "@/components/Tables";
import { WalletFilter } from "@/components/WalletFilter";
import { UtrInput, isUtr } from "@/components/UtrInput";
import { api, errMsg, inr } from "@/lib/api";
import { ADMIN_NAV } from "@/pages/navs";

const ProcessDialog = ({ p, onClose, onDone }) => {
  const [utr, setUtr] = useState("");
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(null);

  const go = async (action) => {
    if (action === "approve" && !isUtr(utr)) return toast.error("Enter 12-digit UTR to approve");
    if (action === "reject" && utr && !isUtr(utr)) return toast.error("UTR must be 12 digits");
    setBusy(action);
    try {
      await api.post(`/admin/payouts/${p.id}/process`, { action, utr, remark });
      toast.success(action === "approve" ? "Payout approved" : "Payout rejected & refunded");
      onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="process-payout-dialog">
        <DialogHeader><DialogTitle className="font-display">Process payout</DialogTitle></DialogHeader>
        {p && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-4 text-sm">
              <div><div className="text-xs text-slate-400">Merchant</div><div className="font-medium">{p.user_name}</div></div>
              <div><div className="text-xs text-slate-400">Amount</div><div className="font-display text-lg font-bold">{inr(p.amount)}</div></div>
              <div><div className="text-xs text-slate-400">Holder</div><div className="font-medium">{p.holder_name}</div></div>
              <div><div className="text-xs text-slate-400">A/c · IFSC</div><div className="font-mono text-xs">{p.account_number}<br />{p.ifsc}</div></div>
            </div>
            <UtrInput testid="process-utr-input" value={utr} onChange={setUtr} required={false} />
            <Input data-testid="process-remark-input" placeholder="Remark (optional)" value={remark} onChange={(e) => setRemark(e.target.value)} />
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="reject-payout-btn" disabled={!!busy} onClick={() => go("reject")} className="flex h-10 items-center justify-center gap-2 rounded-md bg-rose-600 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60">
                {busy === "reject" && <Loader2 className="h-4 w-4 animate-spin" />} Reject
              </button>
              <button data-testid="approve-payout-btn" disabled={!!busy} onClick={() => go("approve")} className="flex h-10 items-center justify-center gap-2 rounded-md bg-emerald-600 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                {busy === "approve" && <Loader2 className="h-4 w-4 animate-spin" />} Approve
              </button>
            </div>
            <p className="text-xs text-slate-400">Approve requires the 12-digit bank UTR. Rejected amount is refunded to the merchant's payout wallet.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AdminPayouts() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);

  const load = () => api.get("/admin/payouts", { params: status === "all" ? {} : { status } }).then((r) => setRows(r.data));
  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <DashLayout nav={ADMIN_NAV} title="Payout Requests" subtitle="Approve or reject merchant payouts with UTR">
      <WalletFilter value={status} onChange={setStatus} options={["pending", "approved", "rejected", "all"]} prefix="payout-filter" />
      <PayoutTable rows={rows} showUser onProcess={setSel} />
      <ProcessDialog p={sel} onClose={() => setSel(null)} onDone={load} />
    </DashLayout>
  );
}
